import { apiFetch } from './client'
import { addLogEntry } from '../ui/debugLog'
import type { LayerVersions } from './types'

/** Upper bound for the call: it runs next to GetCapabilities on boot and must never hold the map up. */
const TIMEOUT_MS = 5000

/**
 * GET /api/layer-versions — one opaque version string per layer, keyed by the
 * qualified layer name (= `LayerInfo.name` = the WMS `LAYERS` param). A layer
 * absent from the map has no known version.
 *
 * Returns `null` on ANY failure (401, 404 while the proxy has not shipped the
 * endpoint yet, 5xx, network, timeout, malformed body). This is a freshness
 * hint, not access control: it never throws and never reaches the enforcement
 * handler, so a failure cannot log the user out or raise a banner. The reason
 * goes to the debug log only (except the expected 401/404).
 */
export async function fetchLayerVersions(): Promise<LayerVersions | null> {
  try {
    const res = await apiFetch('/api/layer-versions', { signal: AbortSignal.timeout(TIMEOUT_MS) })
    const body = (await res.json()) as { versions?: unknown }
    const raw = body?.versions
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      addLogEntry('unknown', res.status, 'layer-versions: malformed body (no "versions" object)')
      return null
    }
    const versions: LayerVersions = {}
    for (const [name, version] of Object.entries(raw)) {
      if (typeof version === 'string' && version !== '') versions[name] = version
    }
    return versions
  } catch (err) {
    const status = (err as { status?: number }).status ?? 0
    // 404 = endpoint not shipped yet, 401 = session gone (the tiles report that
    // themselves): both expected, neither worth a line in the support diagnostic.
    if (status === 404 || status === 401) return null
    const message = err instanceof Error ? err.message : String(err)
    addLogEntry('unknown', status, `layer-versions unavailable: ${message}`)
    return null
  }
}
